/**
 * Draws one resolved series. Heatmap and field go underneath curves and points
 * so a background scalar map never hides the line drawn on top of it.
 */

import type { Scale } from "./scales.ts";
import type {
  Arrow,
  CurveModel,
  FieldModel,
  HeatmapModel,
  PointsModel,
  ResolvedSeries,
} from "./model.ts";
import type { Point } from "./compile.ts";
import { CHROME, rampColor, type ThemeMode } from "./palette.ts";
import type { Layout } from "./geometry.ts";

const LINE_WIDTH = 2;

interface LayerProps {
  readonly series: ResolvedSeries;
  readonly xScale: Scale;
  readonly yScale: Scale;
  readonly layout: Layout;
  readonly mode: ThemeMode;
}

export function SeriesLayer(props: LayerProps) {
  const { series } = props;

  switch (series.body.kind) {
    case "curve":
      return <Curve {...props} body={series.body} />;
    case "points":
      return <Points {...props} body={series.body} />;
    case "field":
      return <Field {...props} body={series.body} />;
    case "heatmap":
      return <Heatmap {...props} body={series.body} />;
    case "failed":
      return null;
  }
}

/** Painting order: fields and maps behind, lines and markers in front. */
export function layerDepth(series: ResolvedSeries): number {
  switch (series.body.kind) {
    case "heatmap":
      return 0;
    case "field":
      return 1;
    case "curve":
      return 2;
    case "points":
      return 3;
    case "failed":
      return 4;
  }
}

function Curve({
  series,
  body,
  xScale,
  yScale,
  layout,
}: LayerProps & { body: CurveModel }) {
  const baseline = clamp(
    yScale.to(inRange(yScale.domain, 0) ? 0 : yScale.domain[0]),
    layout.top,
    layout.top + layout.innerHeight,
  );

  return (
    <g>
      {body.fill &&
        body.segments.map((segment, index) => (
          <path
            key={`fill-${index}`}
            d={`${toPath(segment, xScale, yScale)} L ${xScale.to(segment.at(-1)!.x)} ${baseline} L ${xScale.to(segment[0]!.x)} ${baseline} Z`}
            fill={series.color}
            fillOpacity={0.12}
            stroke="none"
          />
        ))}

      {body.segments.map((segment, index) => (
        <path
          key={`line-${index}`}
          d={toPath(segment, xScale, yScale)}
          fill="none"
          stroke={series.color}
          strokeWidth={LINE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={body.dashed ? "7 5" : undefined}
        />
      ))}
    </g>
  );
}

function Points({
  series,
  body,
  xScale,
  yScale,
  mode,
}: LayerProps & { body: PointsModel }) {
  const surface = CHROME[mode].surface;

  return (
    <g>
      {body.showLine && body.points.length > 1 && (
        <path
          d={toPath(body.points, xScale, yScale)}
          fill="none"
          stroke={series.color}
          strokeWidth={LINE_WIDTH}
          strokeLinejoin="round"
        />
      )}

      {body.yError && (
        <g stroke={series.color} strokeWidth={1.5} opacity={0.85}>
          {body.points.map((point, index) => {
            const error = body.yError![index]!;
            const x = xScale.to(point.x);
            const top = yScale.to(point.y + error);
            const bottom = yScale.to(point.y - error);
            return (
              <g key={`err-${index}`}>
                <line x1={x} x2={x} y1={top} y2={bottom} />
                <line x1={x - 4} x2={x + 4} y1={top} y2={top} />
                <line x1={x - 4} x2={x + 4} y1={bottom} y2={bottom} />
              </g>
            );
          })}
        </g>
      )}

      {body.points.map((point, index) => (
        <circle
          key={`pt-${index}`}
          cx={xScale.to(point.x)}
          cy={yScale.to(point.y)}
          r={body.radius}
          fill={series.color}
          // 2px surface ring keeps overlapping markers countable.
          stroke={surface}
          strokeWidth={2}
        />
      ))}
    </g>
  );
}

function Field({
  series,
  body,
  xScale,
  yScale,
  layout,
}: LayerProps & { body: FieldModel }) {
  // One cell wide at most, so a dense grid never turns into a solid block.
  const cell = layout.innerWidth / Math.sqrt(body.arrows.length);
  const maxLength = cell * 0.82;

  return (
    <g stroke={series.color} strokeWidth={1.6} strokeLinecap="round">
      {body.arrows.map((arrow, index) => {
        const geometry = arrowGeometry(arrow, body, maxLength, xScale, yScale);
        if (!geometry) return null;

        // Magnitude still reads through opacity when directions are normalized.
        const opacity = body.normalize
          ? 0.35 + 0.65 * relativeMagnitude(arrow, body)
          : 0.9;

        return (
          <g key={`arrow-${index}`} opacity={opacity}>
            <line x1={geometry.x1} y1={geometry.y1} x2={geometry.x2} y2={geometry.y2} />
            <path
              d={geometry.head}
              fill={series.color}
              stroke="none"
            />
          </g>
        );
      })}
    </g>
  );
}

interface ArrowGeometry {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  head: string;
}

function arrowGeometry(
  arrow: Arrow,
  body: FieldModel,
  maxLength: number,
  xScale: Scale,
  yScale: Scale,
): ArrowGeometry | null {
  if (arrow.magnitude === 0) return null;

  const scale = body.normalize
    ? 1
    : body.maxMagnitude === 0
      ? 0
      : arrow.magnitude / body.maxMagnitude;

  const length = maxLength * (body.normalize ? 0.9 : Math.max(scale, 0.05));

  // Screen y grows downward; the sign flip here is what makes "up" mean up.
  const ux = arrow.u / arrow.magnitude;
  const uy = -arrow.v / arrow.magnitude;

  const cx = xScale.to(arrow.x);
  const cy = yScale.to(arrow.y);

  const x1 = cx - (ux * length) / 2;
  const y1 = cy - (uy * length) / 2;
  const x2 = cx + (ux * length) / 2;
  const y2 = cy + (uy * length) / 2;

  const headSize = Math.min(length * 0.36, 7);
  const px = -uy;
  const py = ux;
  const head =
    `M ${x2} ${y2} ` +
    `L ${x2 - ux * headSize + px * headSize * 0.45} ${y2 - uy * headSize + py * headSize * 0.45} ` +
    `L ${x2 - ux * headSize - px * headSize * 0.45} ${y2 - uy * headSize - py * headSize * 0.45} Z`;

  return { x1, y1, x2, y2, head };
}

const relativeMagnitude = (arrow: Arrow, body: FieldModel): number =>
  body.maxMagnitude === 0 ? 0 : Math.sqrt(arrow.magnitude / body.maxMagnitude);

function Heatmap({
  body,
  xScale,
  yScale,
  layout,
  mode,
}: LayerProps & { body: HeatmapModel }) {
  const n = body.resolution;
  const cellWidth = layout.innerWidth / n;
  const cellHeight = layout.innerHeight / n;
  const span = body.max - body.min;

  const cells: React.ReactNode[] = [];

  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      const value = body.values[row * n + col]!;
      if (!Number.isFinite(value)) continue;

      const t = (value - body.min) / span;
      cells.push(
        <rect
          key={`c-${row}-${col}`}
          x={layout.left + col * cellWidth}
          // Row 0 is the bottom of the data, but the top of the SVG.
          y={layout.top + (n - 1 - row) * cellHeight}
          width={cellWidth + 0.5}
          height={cellHeight + 0.5}
          fill={rampColor(body.colorScale, t, mode)}
        />,
      );
    }
  }

  return <g shapeRendering="crispEdges">{cells}</g>;
}

/* -------------------------------------------------------------------------- */

export function toPath(points: readonly Point[], xScale: Scale, yScale: Scale): string {
  if (points.length === 0) return "";

  const commands = points.map((point, index) => {
    const x = round(xScale.to(point.x));
    const y = round(yScale.to(point.y));
    return `${index === 0 ? "M" : "L"} ${x} ${y}`;
  });

  return commands.join(" ");
}

const round = (value: number): number => Math.round(value * 100) / 100;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const inRange = (domain: readonly [number, number], value: number): boolean =>
  value >= Math.min(...domain) && value <= Math.max(...domain);
